import type { ApiClient } from '@carbonwebmail/api-client'
import { isEmptyBody, proseBody } from '@carbonwebmail/domain'
import { useState } from 'react'
import type { PreviewDevice } from '../../ui/MessageFrame.js'
import type { PreviewHandoff } from '../../ui/previewTab.js'
import { readPreviewHandoff } from '../../ui/previewTab.js'
import { usePreview } from '../../ui/usePreview.js'

/**
 * The preview tab's state: what it was asked to show, and at what width.
 *
 * Read once, on mount. The message is in this tab's own session storage, so a
 * reload shows the same thing rather than an empty screen — and it is gone the
 * moment the tab is closed.
 *
 * The rendering itself is `usePreview`, the same request the in-form previews
 * make. A second renderer here would be a second answer to what the message
 * says, and the one that drifted would be the one somebody approved.
 */

/** Nothing to preview. A constant, so the hook below is not handed a new object per render. */
const NOTHING = proseBody('')

export type PreviewPane = PreviewDevice | 'text'

export function usePreviewPage({ client }: { readonly client: ApiClient }) {
  const [handoff] = useState<PreviewHandoff | undefined>(readPreviewHandoff)
  const [pane, setPane] = useState<PreviewPane>('desktop')

  const preview = usePreview({
    client,
    subject: handoff?.subject ?? '',
    body: handoff?.body ?? NOTHING,
  })

  /*
   * A message with nothing in it never gets rendered, so waiting for one would
   * be waiting forever — and "Rendering the message…" that never finishes is a
   * screen that looks broken rather than one that is empty.
   */
  const empty = handoff !== undefined && isEmptyBody(handoff.body)

  return { handoff, empty, preview, pane, setPane }
}
